import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Cartao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Rotulo, Selecao } from '../../componentes/formulario';
import { Confirmar } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { del, getTodos, mensagemDeErro } from '../../nucleo/api';
import { dataHora } from '../../nucleo/formato';
import { posicaoNaPagina, recuperarFoco } from '../../nucleo/foco';
import { nomeDaTela } from '../../nucleo/nome-da-tela';
import { useSessao } from '../../nucleo/sessao';
import { Historico } from './Historico';
import type { Visao } from './tipos';

const SELETOR_REMOVER_VISAO = '[data-remover-visao]';

/** M13.4: visões salvas do usuário, por tela, com remoção. E1 (R37-SPEC-M2): com quem cada uma está compartilhada e se está em Relatórios. */
export function Visoes() {
  const { t } = useTranslation('configuracoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [tela, setTela] = useState('');
  const [removendo, setRemovendo] = useState<Visao | null>(null);
  // Review r29 visual (M1): a posição da lixeira que abriu o Confirmar; depois de remover, o foco vai à lixeira que ficou no lugar.
  const posicao = useRef(-1);
  // Review r27 (segurança e robustez, M1): a lista vem paginada e só com metadados; junta as páginas e ordena dentro da tela
  // (padrão primeiro, depois o nome), como a API fazia.
  const q = useQuery({
    queryKey: ['configuracoes', 'visoes'],
    queryFn: async () => {
      const r = await getTodos<Visao>('/configuracoes/visoes');
      return { ...r, itens: [...r.itens].sort((a, b) => a.tela.localeCompare(b.tela) || Number(b.padrao) - Number(a.padrao) || a.nome.localeCompare(b.nome, undefined, { sensitivity: 'base' }) || a.id.localeCompare(b.id)) };
    },
  });
  // Review r28 visual (O4): a tela pelo caminho do menu no idioma da pessoa ("Financeiro > A receber"), não o identificador.
  const nomes = useMemo(() => new Map([...new Set((q.data?.itens ?? []).map((v) => v.tela))].map((x) => [x, nomeDaTela(x, tn)])), [q.data, tn]);
  const telas = useMemo(() => [...nomes.keys()].sort((a, b) => nomes.get(a)!.localeCompare(nomes.get(b)!, undefined, { sensitivity: 'base' })), [nomes]);

  const remover = useMutation({
    mutationFn: (id: string) => del(`/configuracoes/visoes/${id}`),
    onSuccess: async () => {
      setRemovendo(null);
      await Promise.all([qc.invalidateQueries({ queryKey: ['configuracoes', 'visoes'] }), qc.invalidateQueries({ queryKey: ['visoes'] }), qc.invalidateQueries({ queryKey: ['configuracoes', 'historico'] })]);
      toast.sucesso(tn('comum.removido'));
      recuperarFoco({ seletor: SELETOR_REMOVER_VISAO, i: posicao.current, reservas: ['#visoes-tela'] });
    },
    onError: (e) => {
      setRemovendo(null);
      toast.erro(mensagemDeErro(e));
    },
  });

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <Cartao titulo={t('visoes.titulo')} semPadding>
        <p className="border-b border-borda px-4 py-3 text-sm text-tinta-3">{t('visoes.ajuda')}</p>
        <Estado consulta={q}>
          {(d) => {
            if (d.itens.length === 0) return <Vazio texto={t('visoes.vazio')} />;
            const visiveis = tela ? d.itens.filter((v) => v.tela === tela) : d.itens;
            return (
              <>
                <div className="border-b border-borda px-4 py-3">
                  <div className="w-full sm:w-80">
                    <Rotulo id="visoes-tela" rotulo={t('visoes.tela')}>
                      <Selecao id="visoes-tela" vazio={t('visoes.todasTelas')} opcoes={telas.map((x) => ({ valor: x, rotulo: nomes.get(x) ?? x }))} value={tela} onChange={(e) => setTela(e.target.value)} />
                    </Rotulo>
                  </div>
                </div>
                <ul className="divide-y divide-borda">
                  {visiveis.map((v) => (
                    <li key={v.id} className="flex min-w-0 items-center gap-3 px-4 py-3">
                      {/* Review r38 visual (M1): com os selos do E1 (compartilhada, em Relatórios) o nome em `flex-1 min-w-0`
                          ficava com largura 0 e os selos saíam do cartão a 390. A regra da r36: o nome com base própria
                          (`grow basis-24`, sem encolher abaixo dela) e os selos num invólucro que quebra (`min-w-min`, nunca
                          mais estreito que a maior palavra). */}
                      <span className="flex min-w-0 shrink-0 grow basis-24 flex-col">
                        <span className="truncate font-medium" title={v.nome}>{v.nome}</span>
                        <span className="truncate text-xs text-tinta-3" title={nomes.get(v.tela) ?? v.tela} data-tela-visao="">
                          {nomes.get(v.tela) ?? v.tela}
                        </span>
                      </span>
                      {(v.padrao || (v.compartilhamento && v.compartilhamento !== 'privado') || v.em_relatorios) && (
                        <span className="flex min-w-min flex-wrap justify-end gap-1" data-selos-visao="">
                          {v.padrao && <Selo cor="acento" quebra>{t('visoes.padrao')}</Selo>}
                          {v.compartilhamento && v.compartilhamento !== 'privado' && <Selo cor="verde" quebra>{t(`visoes.compartilhamentos.${v.compartilhamento}`)}</Selo>}
                          {v.em_relatorios && <Selo quebra>{t('visoes.emRelatorios')}</Selo>}
                        </span>
                      )}
                      <span className="hidden text-xs text-tinta-3 sm:block">{v.qtd_colunas ? t('visoes.colunas', { count: v.qtd_colunas }) : ''}</span>
                      <span className="hidden text-xs text-tinta-3 md:block">{dataHora(v.created_at)}</span>
                      {s.pode('configuracoes.editar') && (
                        <button type="button" aria-label={`${t('acoes.remover')}: ${v.nome}`} onClick={(e) => {
                            posicao.current = posicaoNaPagina(SELETOR_REMOVER_VISAO, e.currentTarget);
                            setRemovendo(v);
                          }} data-remover-visao="" className="rounded p-1.5 text-tinta-3 hover:text-perigo">
                          <Trash2 className="size-4" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            );
          }}
        </Estado>
      </Cartao>

      <Historico entidade="configuracoes.visao" titulo={t('visoes.historico')} />

      <Confirmar
        aberto={!!removendo}
        aoFechar={() => setRemovendo(null)}
        aoConfirmar={() => removendo && remover.mutate(removendo.id)}
        carregando={remover.isPending}
        perigoso
        titulo={t('visoes.confirmarRemover')}
        texto={t('visoes.confirmarRemoverTexto')}
      />
    </div>
  );
}
